import log from 'loglevel';
import type { Logger } from 'loglevel';

/**
 * Valores admitidos por setItem.
 *
 * La Web Storage API solo guarda cadenas, pero hay llamantes que pasan numeros
 * y booleanos apoyandose en la coercion implicita de localStorage
 * (AppStorage.setInstallDate, setOpenedBefore, setDirectionClicked y los tres
 * setItem de GeoService). La conversion se hace explicita aqui, en un unico
 * sitio, en vez de tocar los seis llamantes: los getters ya leen esos valores
 * de vuelta como cadenas ('true', '0', un timestamp).
 */
export type StorableValue = string | number | boolean;

/**
 * Storage service for user data.
 *
 * Use localStorage or sessionStorage depending on status.
 *
 * Implements most Storage methods plus some utility functions.
 */
class BaseStorage {

    /**
     * Prefix to add to all stored variable names
     */
    static keyPrefix = 'base';

    /**
     * ABSTRACT Gets storage object.
     */
    static storage(): Storage {
      throw new Error("BaseStorage.storage() needs to be overridden. Kind of an abstract method.");
    }

    /**
     * Get logger.
     */
    static logger(): Logger {
      return log.getLogger('BaseStorage');
    }

    /**
     * Implements Storage.getItem()
     */
    static getItem(keyName: string): string | null {
      return this.storage().getItem(this.keyPrefix + '.' + keyName);
    }

    /**
     * Implements Storage.setItem().
     */
    static setItem(keyName: string, keyValue: StorableValue): void {
      this.storage().setItem(this.keyPrefix + '.' + keyName, String(keyValue));
    }

    /**
     * Implements Storage.removeItem()
     */
    static removeItem(keyName: string): void {
      this.storage().removeItem(this.keyPrefix + '.' + keyName);
    }

    /**
     * Implements Storage.clear().
     */
    static clear(): void {
      this.logger().debug('Clear storage');
      this.storage().clear();
    }

    /**
     * Save object to storage.
     *
     * @param objData Data to be stored.
     */
    static setObject(keyName: string, objData: unknown): void {
      this.setItem(keyName, JSON.stringify(objData));
    }

    /**
     * Get object from storage.
     *
     * El JSON almacenado no se valida: el parametro de tipo declara lo que el
     * llamante espera encontrar, y es el llamante quien debe comprobarlo.
     * @see AppStorage.getRateAppData
     */
    static getObject<T = unknown>(keyName: string): T | null {
      const rawData = this.getItem(keyName);
      if (rawData) {
        return JSON.parse(rawData) as T;
      }
      else {
        return null;
      }
    }

}

export default BaseStorage;
